import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ChevronDown, Search, HelpCircle, Mail } from 'lucide-react';

const faqData = [
  {
    question: "What is Ub Creation",
    answer: "Ub Creation is a game development company dedicated to creating immersive and high quality gaming experiences for Android and iOS platforms.",
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    question: "What services do you offer?",
    answer: "We offer full cycle game development, UI/UX design, game animation, custom SDK integration, performance optimization, device testing, and monetization strategy.",
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    question: "How can I contact you?",
    answer: "You can reach us through the contact form on our website, email us at ubcreationspvtltd@gmail.com, or call us at +92 301 3717722.",
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    question: "What platforms do you develop games for?",
    answer: "We develop games for both Android and iOS using Unity, Unreal Engine, and native technologies to ensure top performance on all devices.",
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    question: "Do you offer post‑launch support?",
    answer: "Yes! We provide ongoing maintenance, updates, and live operations to keep your game running smoothly after launch.",
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    question: "What is your development process?",
    answer: "We follow a streamlined process: concept & design, prototyping, development, rigorous testing, and final launch. We keep you involved at every stage.",
    icon: <HelpCircle className="w-5 h-5" />
  }
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const filteredFaqs = faqData.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 30 + 10,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 5,
    opacity: Math.random() * 0.3 + 0.1,
  }));

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-white via-gray-50 to-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Background orbs and particles (existing) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-3xl" />
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-r from-red-400/20 to-rose-400/20 blur-xl animate-float"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              opacity: particle.opacity,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
      </div>

      {/* New decorative elements – unique design */}
      {/* Left: soft glowing gradient circle */}
      <div className="absolute left-0 top-1/4 w-48 h-48 md:w-64 md:h-64 rounded-full bg-gradient-to-r from-red-500/20 via-rose-500/20 to-red-500/20 blur-2xl animate-float-slow pointer-events-none z-0" />
      <div className="absolute left-0 top-1/4 w-48 h-48 md:w-64 md:h-64 rounded-full bg-gradient-to-r from-red-500/10 via-rose-500/10 to-red-500/10 blur-3xl animate-pulse-slow pointer-events-none z-0" />

      {/* Right: rotating ring */}
      <div className="absolute right-0 bottom-1/4 w-56 h-56 md:w-72 md:h-72 pointer-events-none z-0">
        <div className="absolute inset-0 rounded-full border-2 border-red-300/30 animate-spin-slow" />
        <div className="absolute inset-4 rounded-full border border-red-400/20 animate-spin-slow-reverse" />
        <div className="absolute inset-8 rounded-full bg-red-500/10 blur-sm animate-pulse" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-bold">
            <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
              Frequently Asked Questions
            </span>
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our services and game development.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="mb-8"
        >
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white/60 backdrop-blur-md rounded-xl border border-white/30 focus:border-red-400 focus:ring-2 focus:ring-red-300 outline-none transition-all text-gray-800 placeholder-gray-500"
            />
          </div>
        </motion.div>

        {/* FAQ Items (unchanged) */}
        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No matching questions found.</p>
          ) : (
            filteredFaqs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="group relative"
              >
                <div
                  className={`relative bg-white/60 backdrop-blur-md rounded-2xl p-5 transition-all duration-500 ${
                    activeIndex === index
                      ? 'shadow-xl shadow-red-100/30 border-red-300/50'
                      : 'border border-white/30 hover:border-red-300/30 hover:shadow-lg hover:shadow-red-100/20'
                  }`}
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleAnswer(index)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-red-500 group-hover:text-red-600 transition-colors">
                        {item.icon}
                      </span>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-red-600 transition-colors">
                        {item.question}
                      </h3>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-red-500 transition-transform duration-300 ${
                        activeIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </div>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p className="text-gray-600 text-base leading-relaxed border-t border-gray-200/50 pt-3">
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Call to Action (unchanged) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-md rounded-full px-6 py-3 border border-white/30">
            <Mail className="w-5 h-5 text-red-500" />
            <span className="text-gray-700">Still have questions?</span>
            <a
              href="/contact"
              className="ml-2 text-red-600 font-semibold hover:underline"
            >
              Contact us
            </a>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-15px) translateX(5px); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 25s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default FAQ;